import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Footer } from '../components/Footer';
import downloadHeroImage from '../assets/marketplace/market-hero-download-selected-box.png';
import './downloadCenter.scss';
const PATCH_ROWS = [
    {
        version: 'YonBIP V5 金盘',
        patch: 'aPaaS_iuap-yonbuilder-designer_iuap.7.0.2507_20250824-34_QP20260929-13833',
    },
    {
        version: 'YonBIP V5-251130',
        patch: 'aPaaS_iuap-yonbuilder-designer_iuap.7.0.2507_20250824-34_QP20260930-13848',
    },
    {
        version: 'YonBIP V5-260330',
        patch: 'aPaaS_iuap-yonbuilder-designer_iuap.7.0.2507_20250824-34_QP20260930-13849',
    },
    {
        version: 'YonBIP V6',
        patch: 'aPaaS_iuap-yonbuilder-designer_iuap.7.5.2605.20260912-180_QP20260929-4206',
    },
];
function PatchModal({ onClose }) {
    const backdropRef = useRef(null);
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape')
                onClose();
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [onClose]);
    const handleBackdropClick = (e) => {
        if (e.target === backdropRef.current)
            onClose();
    };
    return (_jsx("div", { className: "patch-modal-backdrop", ref: backdropRef, onClick: handleBackdropClick, children: _jsxs("div", { className: "patch-modal", role: "dialog", "aria-modal": "true", "aria-labelledby": "patch-modal-title", children: [_jsx("button", { className: "patch-modal-close", onClick: onClose, "aria-label": "\u5173\u95ED\u5F39\u7A97", children: "\u00D7" }), _jsx("h3", { id: "patch-modal-title", children: "\u91CD\u8981\u5B89\u88C5\u63D0\u9192" }), _jsx("p", { children: "\u8BF7\u6839\u636E\u4EA7\u54C1\u7248\u672C\u5B89\u88C5\u5BF9\u5E94\u7684 YonBIP \u8865\u4E01\uFF0C\u7248\u672C\u4E0E\u8865\u4E01\u53F7\u5BF9\u7167\u5982\u4E0B\uFF1A" }), _jsxs("table", { className: "patch-table", children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "YonBIP \u7248\u672C" }), _jsx("th", { children: "\u8865\u4E01\u53F7" })] }) }), _jsx("tbody", { children: PATCH_ROWS.map((row) => (_jsxs("tr", { children: [_jsx("td", { className: "patch-table-version", children: row.version }), _jsx("td", { className: "patch-table-patch", children: row.patch })] }, row.version))) })] })] }) }));
}
const DOWNLOAD_LINKS_API = 'https://package.yonyoucloud.com/api/yoncode/dict/list?dict_type=download_address';
const UPDATE_RELEASES_API = 'https://package.yonyoucloud.com/api/update/releases/list';
const releasePlatformKeys = ['windows', 'mac'];
const channelOrder = {
    alpha: 0,
    beta: 1,
    rc: 2,
    release: 3,
};
const basePlatforms = [
    {
        key: 'windows',
        name: 'Windows',
        arch: 'x86_64',
        description: '适用于 Windows 开发环境。',
        primaryActions: [{ label: '下载正式版（x86_64）', downloadKey: 'windows-x64' }],
    },
    {
        key: 'mac',
        name: 'macOS',
        arch: 'arm64 / x86_64',
        description: '针对 Intel 与 Apple Silicon 设备优化。',
        primaryActions: [
            { label: '下载 Apple Silicon', downloadKey: 'darwin-arm64' },
            { label: '下载 Intel', downloadKey: 'darwin-x64' },
        ],
    },
    {
        key: 'linux',
        name: 'Linux',
        arch: 'x86_64',
        description: '支持主流 Linux 发行版。',
        primaryActions: [{ label: '下载正式版（x86_64）', downloadKey: 'linux-x64' }],
        versionsDisabled: true,
    },
];
function createEmptyReleaseVersionGroups() {
    return {
        windows: [],
        mac: [],
    };
}
function getReleasePlatformKey(platform) {
    const normalized = platform.toLowerCase();
    if (normalized.includes('darwin') || normalized.includes('drawin') || normalized.includes('mac'))
        return 'mac';
    if (normalized.includes('win'))
        return 'windows';
    return null;
}
function getArtifactDownloadKey(platform) {
    const normalized = platform.toLowerCase();
    if (normalized.includes('darwin-arm64'))
        return 'darwin-arm64';
    if (normalized.includes('darwin') || normalized.includes('drawin'))
        return 'darwin-x64';
    if (normalized.includes('win'))
        return 'windows-x64';
    return normalized;
}
function getVersionParts(value) {
    const versionParts = value.trim().match(/\d+/g);
    if (!versionParts)
        return [];
    return versionParts.map((part) => Number.parseInt(part, 10));
}
function normalizeChannel(channel) {
    return channel.trim().toLowerCase();
}
function getFormatLabel(format) {
    const normalized = format.toLowerCase();
    if (normalized.includes('exe'))
        return '.exe';
    if (normalized.includes('zip'))
        return '.zip';
    if (normalized.includes('dmg'))
        return '.dmg';
    return `.${format.replace(/^\./, '')}`;
}
function getProductVersionDisplay(productVersion) {
    const trimmed = productVersion.trim();
    const channelIndex = trimmed.search(/[.-](alpha|beta|rc|release)([.-]\d+)?/i);
    if (channelIndex > 0) {
        const baseVersion = trimmed.slice(0, channelIndex);
        const suffix = trimmed.slice(channelIndex).replace(/^[.-]/, '');
        return {
            title: suffix,
            versionTag: `v${baseVersion}`,
        };
    }
    // Fallback: split at the 3rd numeric segment (semver base) so that
    // versions like "1.115.0-6.0.0-202605" display as tag "v1.115.0" / title "6.0.0-202605"
    // instead of rendering the full string as the title with no tag.
    const allParts = trimmed.match(/\d+|[.-]|[^.\d-]+/g);
    if (allParts) {
        let numericCount = 0;
        let splitIndex = -1;
        for (let i = 0; i < allParts.length; i++) {
            if (/^\d+$/.test(allParts[i])) {
                numericCount++;
                if (numericCount === 3) {
                    splitIndex = i + 1;
                    break;
                }
            }
        }
        if (splitIndex > 0 && splitIndex < allParts.length) {
            const baseVersion = allParts.slice(0, splitIndex).join('').replace(/^[vV]+/, '');
            const rest = allParts.slice(splitIndex).join('').replace(/^[.-]/, '');
            if (baseVersion && rest) {
                return {
                    title: rest,
                    versionTag: `v${baseVersion}`,
                };
            }
        }
    }
    return {
        title: trimmed,
        versionTag: '',
    };
}
function appendArtifact(candidate, artifact) {
    const formatLabel = getFormatLabel(artifact.format);
    const downloadKey = getArtifactDownloadKey(artifact.platform);
    if (!candidate.artifacts.some((item) => item.label === formatLabel)) {
        candidate.artifacts.push({
            href: artifact.downloadUrl,
            label: formatLabel,
            downloadKey,
        });
    }
}
function buildReleaseVersionGroups(releases) {
    const groups = createEmptyReleaseVersionGroups();
    const byPlatformChannelVersion = new Map();
    releases
        .filter((release) => !release.status || release.status === 'active')
        .forEach((release) => {
        const artifact = release.artifact;
        if (!artifact?.downloadUrl || !artifact.platform || !artifact.format)
            return;
        const platformKey = getReleasePlatformKey(artifact.platform);
        if (!platformKey)
            return;
        const channel = normalizeChannel(release.channel);
        if (!channel)
            return;
        const productVersion = release.productVersion || release.tagName || release.releaseLine;
        if (!productVersion)
            return;
        const mapKey = `${platformKey}|${channel}|${productVersion}`;
        const existing = byPlatformChannelVersion.get(mapKey);
        if (existing) {
            appendArtifact(existing, artifact);
            return;
        }
        const versionDisplay = getProductVersionDisplay(productVersion);
        const candidate = {
            artifacts: [],
            badge: channel,
            platformKey,
            productVersion,
            sortTime: Date.parse(release.createdAt) || 0,
            title: versionDisplay.title,
            versionParts: getVersionParts(productVersion),
            versionTag: versionDisplay.versionTag,
        };
        appendArtifact(candidate, artifact);
        byPlatformChannelVersion.set(mapKey, candidate);
    });
    byPlatformChannelVersion.forEach((item) => {
        groups[item.platformKey].push({
            actions: item.artifacts.map((artifact) => ({
                href: artifact.href,
                label: artifact.label,
                downloadKey: artifact.downloadKey,
            })),
            badge: item.badge,
            title: item.title,
            versionTag: item.versionTag,
        });
    });
    releasePlatformKeys.forEach((platformKey) => {
        groups[platformKey] = groups[platformKey].filter((version) => {
            const parts = getVersionParts(version.versionTag || version.title);
            return parts.length >= 2 && parts[0] === 1 && parts[1] === 116;
        });
        groups[platformKey].sort((a, b) => {
            const aChannel = normalizeChannel(a.badge);
            const bChannel = normalizeChannel(b.badge);
            const channelDelta = (channelOrder[bChannel] ?? 99) - (channelOrder[aChannel] ?? 99);
            if (channelDelta !== 0)
                return channelDelta;
            return b.title.localeCompare(a.title, undefined, { numeric: true });
        });
        groups[platformKey] = groups[platformKey].slice(0, 6);
    });
    return groups;
}
function getLatestReleaseData(versions) {
    const hrefs = {};
    const formats = {};
    const release = versions.find((v) => normalizeChannel(v.badge) === 'release');
    const target = release || versions[0];
    if (target) {
        target.actions.forEach((action) => {
            if (!action.downloadKey || !action.href)
                return;
            if (!hrefs[action.downloadKey]) {
                hrefs[action.downloadKey] = action.href;
            }
            if (!formats[action.downloadKey]) {
                formats[action.downloadKey] = [];
            }
            formats[action.downloadKey].push({ label: action.label, href: action.href });
        });
        Object.keys(formats).forEach((key) => {
            formats[key].sort((a, b) => {
                const aExe = a.label.toLowerCase().includes('exe') ? 0 : 1;
                const bExe = b.label.toLowerCase().includes('exe') ? 0 : 1;
                return aExe - bExe;
            });
        });
    }
    return { hrefs, formats };
}
function buildDownloadPlatforms(releaseVersions) {
    return basePlatforms.map((platform) => {
        if (platform.versionsDisabled || platform.key === 'linux') {
            return {
                ...platform,
                versions: [],
            };
        }
        const platformKey = platform.key;
        const versions = releaseVersions[platformKey];
        const { hrefs, formats } = getLatestReleaseData(versions);
        // Windows 平台暂时只支持 exe 安装
        if (platform.key === 'windows') {
            Object.keys(formats).forEach((key) => {
                formats[key] = formats[key].filter((f) => f.label.toLowerCase().includes('exe'));
            });
        }
        return {
            ...platform,
            versions,
            releaseHrefs: hrefs,
            releaseFormats: formats,
        };
    });
}
function DownloadActionLink({ action, links, className = 'download-link', disabledClassName = 'download-link--disabled', }) {
    const href = action.href || (action.downloadKey ? links[action.downloadKey] : '');
    const resolvedClassName = action.disabled || !href
        ? `${className} ${disabledClassName}`
        : className;
    if (action.disabled || !href) {
        return (_jsx("span", { className: resolvedClassName, "aria-disabled": "true", children: action.label }));
    }
    return (_jsx("a", { className: resolvedClassName, href: href, children: action.label }));
}
function ChevronIcon() {
    return (_jsx("svg", { className: "download-toggle-icon", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", focusable: "false", children: _jsx("path", { d: "M6 9l6 6 6-6" }) }));
}
function PlatformIcon({ type }) {
    if (type === 'windows') {
        return (_jsxs("svg", { className: "device-icon device-icon--windows", viewBox: "0 0 48 48", "aria-hidden": "true", children: [_jsx("path", { className: "icon-fill", d: "M8 11.4 21.6 9.5v13.2H8V11.4Z" }), _jsx("path", { className: "icon-fill", d: "M25 9 40 7v15.7H25V9Z" }), _jsx("path", { className: "icon-fill", d: "M8 25.3h13.6v13.2L8 36.6V25.3Z" }), _jsx("path", { className: "icon-fill", d: "M25 25.3h15V41l-15-2.1V25.3Z" })] }));
    }
    if (type === 'mac') {
        return (_jsx("svg", { className: "device-icon device-icon--mac", viewBox: "0 0 1024 1024", "aria-hidden": "true", children: _jsx("path", { className: "icon-fill", d: "M645.289723 165.758826C677.460161 122.793797 701.865322 62.036894 693.033384 0c-52.607627 3.797306-114.089859 38.61306-149.972271 84.010072-32.682435 41.130375-59.562245 102.313942-49.066319 161.705521 57.514259 1.834654 116.863172-33.834427 151.294929-79.956767zM938.663644 753.402663c-23.295835 52.820959-34.517089 76.415459-64.511543 123.177795-41.855704 65.279538-100.905952 146.644295-174.121433 147.198957-64.980873 0.725328-81.748754-43.30636-169.982796-42.751697-88.234042 0.46933-106.623245 43.605024-171.732117 42.965029-73.130149-0.682662-129.065752-74.026142-170.964122-139.348347-117.11917-182.441374-129.44975-396.626524-57.172928-510.545717 51.327636-80.895427 132.393729-128.212425 208.553189-128.212425 77.482118 0 126.207106 43.519692 190.377318 43.519692 62.292892 0 100.137957-43.647691 189.779989-43.647691 67.839519 0 139.732344 37.802399 190.889315 103.03927-167.678812 94.036667-140.543004 339.069598 28.885128 404.605134z" }) }));
    }
    return (_jsx("svg", { className: "device-icon device-icon--linux", viewBox: "0 0 48 48", "aria-hidden": "true", children: _jsx("path", { className: "icon-fill", d: "M24 5.5c-5.8 0-10 5.1-10 12.2 0 2.6.5 4.8 1.3 6.8-3.1 2.3-5.1 5.9-5.1 10.1 0 5.1 3.3 8 8.4 8 2.2 0 4.1-.8 5.4-2.1 1.3 1.3 3.2 2.1 5.4 2.1 5.1 0 8.4-2.9 8.4-8 0-4.2-2-7.8-5.1-10.1.8-2 1.3-4.2 1.3-6.8 0-7.1-4.2-12.2-10-12.2Zm-3.2 11.1c.9 0 1.7.9 1.7 2s-.8 2-1.7 2-1.7-.9-1.7-2 .8-2 1.7-2Zm6.4 0c.9 0 1.7.9 1.7 2s-.8 2-1.7 2-1.7-.9-1.7-2 .8-2 1.7-2Zm-3.2 8.1c1.7 0 3.1.7 4 1.8-.9.8-2.2 1.2-4 1.2s-3.1-.4-4-1.2c.9-1.1 2.3-1.8 4-1.8Zm-6.2 4.4c.9 1.2 2.6 2.5 6.2 2.5s5.3-1.3 6.2-2.5c1.8 1.4 2.9 3.4 2.9 5.8 0 2.5-1.3 3.9-3.9 3.9-1.7 0-2.9-.8-3.5-2.4h-3.4c-.6 1.6-1.8 2.4-3.5 2.4-2.6 0-3.9-1.4-3.9-3.9 0-2.4 1.1-4.4 2.9-5.8Z" }) }));
}
function DownloadVisual() {
    return (_jsx("div", { className: "market-product-visual resource-market-visual resource-market-visual--download resource-market-visual--premium download-product-visual", "aria-label": "\u4E0B\u8F7D\u4E2D\u5FC3\u5B89\u88C5\u5305\u89C6\u89C9\u56FE", role: "img", children: _jsx("div", { className: "download-commercial-visual", children: _jsx("img", { src: downloadHeroImage, alt: "", width: "1216", height: "832", decoding: "async", loading: "eager" }) }) }));
}
function DownloadPlatformCard({ platform, links }) {
    const resolvedActions = platform.primaryActions.map((action) => {
        const releaseHref = action.downloadKey && platform.releaseHrefs?.[action.downloadKey];
        const formatOptions = action.downloadKey && platform.releaseFormats?.[action.downloadKey];
        if (!releaseHref)
            return action;
        return { ...action, href: releaseHref, formatOptions: formatOptions && formatOptions.length > 0 ? formatOptions : undefined };
    });
    const [selectedFormats, setSelectedFormats] = useState({});
    return (_jsxs("article", { className: "download-card reveal", children: [_jsxs("div", { className: "download-head", children: [_jsx("span", { className: "device-icon-shell", children: _jsx(PlatformIcon, { type: platform.key }) }), _jsx("span", { children: platform.name }), _jsx("strong", { children: platform.arch })] }), _jsx("p", { children: platform.description }), resolvedActions.length > 1 ? (_jsx("div", { className: "split-actions", children: resolvedActions.map((action) => (_jsx(DownloadActionLink, { action: action, links: links }, action.label))) })) : resolvedActions[0].formatOptions ? (() => {
                const action = resolvedActions[0];
                const options = action.formatOptions;
                const currentFormat = selectedFormats[0] || options[0].label;
                const selectedHref = options.find((o) => o.label === currentFormat)?.href || options[0].href;
                return (_jsxs("div", { className: "download-format-selector", children: [_jsx("a", { className: "download-link", href: selectedHref, children: action.label }), _jsxs("label", { className: `download-format-pick${options.length <= 1 ? ' download-format-pick--disabled' : ''}`, children: [_jsx("span", { className: "download-format-pick-label", children: currentFormat }), _jsx("select", { disabled: options.length <= 1, value: currentFormat, onChange: (e) => setSelectedFormats((prev) => ({ ...prev, [0]: e.target.value })), children: options.map((option) => (_jsx("option", { value: option.label, children: option.label }, option.label))) }), _jsx(ChevronIcon, {})] })] }));
            })() : (_jsx(DownloadActionLink, { action: resolvedActions[0], links: links }))] }));
}
export default function DownloadCenterPage() {
    const pageRef = useRef(null);
    const [links, setLinks] = useState({});
    const [releaseVersions, setReleaseVersions] = useState(() => createEmptyReleaseVersionGroups());
    const [showPatchModal, setShowPatchModal] = useState(false);
    const downloadPlatforms = useMemo(() => buildDownloadPlatforms(releaseVersions), [releaseVersions]);
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
        const revealItems = pageRef.current?.querySelectorAll('.reveal') || [];
        revealItems.forEach((item, index) => {
            item.style.transitionDelay = `${Math.min(index * 70, 280)}ms`;
            item.classList.add('is-visible');
        });
    }, []);
    useEffect(() => {
        let active = true;
        fetch(DOWNLOAD_LINKS_API)
            .then((res) => res.json())
            .then((res) => {
            if (!active)
                return;
            const list = Array.isArray(res.data) ? res.data : [];
            const rawValue = list[0]?.dict_value;
            if (!rawValue)
                return;
            const parsedLinks = JSON.parse(rawValue);
            setLinks(parsedLinks);
        })
            .catch((error) => {
            console.error(error);
        });
        return () => {
            active = false;
        };
    }, []);
    useEffect(() => {
        let active = true;
        fetch(UPDATE_RELEASES_API)
            .then((res) => res.json())
            .then((res) => {
            if (!active)
                return;
            const releases = Array.isArray(res.releases) ? res.releases : [];
            setReleaseVersions(buildReleaseVersionGroups(releases));
        })
            .catch((error) => {
            console.error(error);
        });
        return () => {
            active = false;
        };
    }, []);
    return (_jsxs(_Fragment, { children: [_jsx("main", { className: "site-main download-center-page resource-market-page", ref: pageRef, children: _jsxs("section", { className: "download-center-page-shell resource-market-shell", children: [_jsx("div", { className: "section-shell", children: _jsxs("div", { className: "resource-market-hero resource-market-hero--download download-market-hero reveal", children: [_jsxs("div", { className: "resource-market-copy download-market-hero-copy", children: [_jsx("p", { className: "eyebrow", children: "YONCODE DOWNLOAD CENTER" }), _jsxs("h1", { children: [_jsx("span", { children: "\u4E0B\u8F7D" }), "\u4E2D\u5FC3"] }), _jsx("p", { children: "\u7EDF\u4E00\u83B7\u53D6 YonCode \u5B89\u88C5\u5305\uFF0C\u6309\u64CD\u4F5C\u7CFB\u7EDF\u548C\u82AF\u7247\u67B6\u6784\u9009\u62E9\u9002\u5408\u56E2\u961F\u7EDF\u4E00\u5B89\u88C5\u3001\u7070\u5EA6\u9A8C\u8BC1\u6216\u672C\u5730\u5F00\u53D1\u7684\u4E0B\u8F7D\u5165\u53E3\u3002" })] }), _jsx(DownloadVisual, {})] }) }), _jsxs("div", { className: "section-shell download-center-content", children: [_jsxs("div", { className: "section-heading reveal", children: [_jsx("p", { className: "eyebrow", children: "DOWNLOAD OPTIONS" }), _jsx("h2", { children: "\u9009\u62E9\u4F60\u7684\u5E73\u53F0" }), _jsxs("p", { children: ["\u8BF7\u6839\u636E\u5F53\u524D\u4EA7\u54C1\u7248\u672C\uFF0C\u63D0\u524D\u4E0B\u8F7D\u914D\u5957\u8865\u4E01\u540E\u518D\u7EE7\u7EED\u4F7F\u7528", _jsx("button", { className: "patch-detail-trigger", onClick: () => setShowPatchModal(true), children: "\u67E5\u770B\u8BE6\u60C5" })] })] }), _jsx("div", { className: "download-grid download-center-grid download-platform-grid", children: downloadPlatforms.map((platform) => (_jsx(DownloadPlatformCard, { platform: platform, links: links }, platform.key))) })] })] }) }), _jsx(Footer, {}), showPatchModal && _jsx(PatchModal, { onClose: () => setShowPatchModal(false) })] }));
}
